import { ACTIONS, usePFTracking } from '@/hooks/tracking';
import {
  Body,
  Box,
  CrossLightIcon,
  DrawerBottomSheet,
  Flex,
  Heading,
  Link,
  useDrawer,
} from '@edreamsodigeo/prisma-design-system';
import React from 'react';
import { NeedAppSubtitleBoldStyle } from './MoreInfoBanner.utils';
import { Trans, useBrand, useTranslation } from '@edreamsodigeo/frontend-core';
import { PriceStyle } from '../PriceStyle';
import { brandNames } from '@/constants/brandNames';

export const MoreInfoDrawerBanner = ({
  days,
  cap,
  deposit,
  closeContent = () => {},
  openDrawer,
}) => {
  const { track } = usePFTracking();
  const { state, transitionDuration } = useDrawer({
    isOpen: openDrawer,
    transitionDuration: 300,
  });

  const closeHandler = () => {
    track(ACTIONS.LEARN_MORE.ONCLOSE);
    closeContent();
  };

  return (
    <DrawerBottomSheet state={state} transitionDuration={transitionDuration}>
      <MoreInfoDrawerHeader closeContent={closeHandler} />
      <MoreInfoBannerMobile cap={cap} deposit={deposit} days={days} />
    </DrawerBottomSheet>
  );
};

const MoreInfoDrawerHeader = ({ closeContent = () => {} }) => {
  const { t } = useTranslation();
  return (
    <>
      <Box py="big" px="base" position="relative">
        <Flex flexDirection="row" justifyContent="center" alignItems="center">
          <Box position="absolute" sx={{ left: '16px' }}>
            <CrossLightIcon
              size="medium"
              color="primary.50"
              cursor="pointer"
              onClick={closeContent}
              aria-label="closeIcon"
            />
          </Box>
          <Box flexGrow={3}>
            <Heading
              textAlign="center"
              type="4"
              weight="medium"
              color="neutral.90"
            >
              {t('pricefreeze.moreinfo.header.title')}
            </Heading>
          </Box>
        </Flex>
      </Box>
    </>
  );
};
const MoreInfoBannerNeedAppParag = ({ days }) => {
  const { t } = useTranslation();
  return (
    <Box pb="base">
      <Box pb="1x_small">
        <Heading type="5" weight="medium" color="neutral.90">
          {t('pricefreeze.moreinfo.needapp.title')}
        </Heading>
      </Box>
      <Box pb="2x_small">
        <Body type="small" weight="regular" color="neutral.90">
          <Trans
            i18nKey="pricefreeze.moreinfo.needapp.trip.subtitle.normal"
            components={{
              bold: (
                <NeedAppSubtitleBoldStyle
                  type="small"
                  content={t('pricefreeze.moreinfo.needapp.subtitle.bold')}
                />
              ),
              days: `${days}`,
            }}
          />
        </Body>
      </Box>
    </Box>
  );
};

const MoreInfoTitle = ({ children }) => {
  return (
    <Box pb="1x_small">
      <Heading type="5" weight="medium" color="neutral.90">
        {children}
      </Heading>
    </Box>
  );
};

const MoreInfoSubtitle = ({ children }) => {
  return (
    <Box pb="2x_small">
      <Body type="small" weight="regular" color="neutral.90">
        {children}
      </Body>
    </Box>
  );
};

const MoreInfoBannerParag1PrimeFirst = ({ cap }) => {
  const brand = useBrand();
  const brandName = brandNames[brand];

  return (
    <Box pb="base">
      <MoreInfoTitle>
        <Trans
          i18nKey="pricefreeze.moreinfo.coverage.title"
          components={{
            price: <PriceStyle money={cap} rounded />,
          }}
        />
      </MoreInfoTitle>
      <MoreInfoSubtitle>
        <Trans
          i18nKey="pricefreeze.primefirst.moreinfo.coverage.subtitle"
          components={{
            brand: brandName,
            price: <PriceStyle money={cap} rounded />,
          }}
        />
      </MoreInfoSubtitle>
    </Box>
  );
};

export const MoreInfoBannerParag1Variation = MoreInfoBannerParag1PrimeFirst;

const MoreInfoBannerParag2PrimeFirst = () => {
  const { t } = useTranslation();

  return (
    <Box pb="base">
      <MoreInfoTitle>
        {t('pricefreeze.primefirst.moreinfo.book.title')}
      </MoreInfoTitle>
      <MoreInfoSubtitle>
        {t('pricefreeze.primefirst.moreinfo.book.subtitle')}
      </MoreInfoSubtitle>
    </Box>
  );
};

export const MoreInfoBannerParag2Variation = MoreInfoBannerParag2PrimeFirst;

const MoreInfoBannerParag3PrimeFirst = ({ days }) => {
  return <MoreInfoBannerNeedAppParag days={days} />;
};

export const MoreInfoBannerParag3Variation = MoreInfoBannerParag3PrimeFirst;

const FullTermsAndConditionsLink = () => {
  const { t } = useTranslation();
  const { track } = usePFTracking();
  return (
    <Box pb="1x_big" sx={{ textAlign: 'center' }}>
      <Body
        inline
        type="xSmall"
        weight="regular"
        textAlign="center"
        onClick={() => track(ACTIONS.LEARN_MORE.CLICK.TERMS)}
      >
        <Link
          href={t('terms.pricefreeze.url')}
          target={'_blank'}
          type={'dotted'}
        >
          {t('pricefreeze.moreinfo.termsandconditions.link')}
        </Link>
      </Body>
    </Box>
  );
};

export const MoreInfoBannerMobile = ({ days, cap, deposit }) => {
  return (
    <>
      <Box px="base" pt="base">
        {deposit.amount !== 0 ? (
          <MoreInfoBannerParag1Variation
            days={days}
            cap={cap}
            deposit={deposit}
          />
        ) : (
          <MoreInfoBannerParag1PrimeFirst cap={cap} />
        )}
        <MoreInfoBannerParag2Variation days={days} />
        <MoreInfoBannerParag3Variation days={days} />
        <FullTermsAndConditionsLink />
      </Box>
    </>
  );
};
